import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Link } from 'react-router-dom'
import { push } from 'connected-react-router'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import {
    Dialog,
    InputGroup,
    Button,
    Classes,
    Spinner,
    Tag,
    MenuItem,
    Menu,
    Card,
    Intent,
} from '@blueprintjs/core'
import moment from 'moment'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons/faCirclePlus'
import { faTrafficLight } from '@fortawesome/pro-solid-svg-icons/faTrafficLight'
import { faFileExport } from '@fortawesome/pro-solid-svg-icons'
import { faEllipsisVertical } from '@fortawesome/pro-light-svg-icons/faEllipsisVertical'

import ConfirmDialog from 'src/components/ConfirmDialog'
import CustomTag from 'src/components/CustomTag'
import DataPlaceholder from 'src/components/DataPlaceholder'
import IconMenu from 'src/components/IconMenu'
import List from 'src/components/List'
import PageTabs from 'src/components/PageTabs'
import {
    getResolvedSelectedTabId,
    getVisibleTabs,
} from 'src/components/PageTabs/utils'
import CreateTemplateModal from 'src/components/Template/CreateTemplateModal'
import {
    RUNBOOKS_TEMPLATES_MAINTAIN,
    isAllowed,
} from 'src/components/auth/permissions'
import AdminEditingDialog from 'src/routes/Runbooks/AdminEditingDialog'
import CreateRunbookModal from 'src/routes/Runbook/CreateRunbookModal'
import RunbooksFilters, {
    lifecycleIcons,
} from 'src/routes/Runbooks/RunbooksFilters'
import RunbooksTemplates from 'src/routes/Runbooks/RunbooksTemplates'
import { setActiveRunbook } from 'src/store/modules/ui/runbook'
import * as runbookModules from 'src/store/modules/runbooks'
import * as actionTypes from 'src/constants/actionTypes'
import * as paths from 'src/constants/paths'
import {
    RESET_CREATE_RUNBOOK,
    RESET_CREATE_TEMPLATE,
} from 'src/constants/actionTypes'
import { UserDataContext } from 'src/HOCs/withAuth'
import { TIMESTAMP_FORMAT } from 'src/utils/reducer'
import Pagination from 'src/utils/pagination'
import noRunbooksImage from 'src/assets/icons/no-runbooks.svg'
import styles from './runbooks.module.scss'

const RUNBOOKS_TABS = Object.freeze([
    Object.freeze({
        id: 'all-runbooks',
        title: 'All Runbooks',
    }),
    Object.freeze({
        id: 'templates',
        title: 'Templates',
        permissions: Object.freeze({
            anyOf: Object.freeze([RUNBOOKS_TEMPLATES_MAINTAIN]),
        }),
    }),
])

class RunbooksList extends React.Component {
    static contextType = UserDataContext

    static propTypes = {
        runbooks: PropTypes.arrayOf(PropTypes.object).isRequired,
        pathname: PropTypes.string,
        loading: PropTypes.bool.isRequired,
        customerFilter: PropTypes.array.isRequired,
        page: PropTypes.number.isRequired,
        totalElements: PropTypes.number.isRequired,
        totalPages: PropTypes.number.isRequired,
        perPage: PropTypes.number.isRequired,
        currentUserIsGlobalAdmin: PropTypes.bool,
        dispatch: PropTypes.func.isRequired,
        actions: PropTypes.shape({
            deleteRunbook: PropTypes.func.isRequired,
            getRunbooksFromServer: PropTypes.func.isRequired,
            createRunbookTemplate: PropTypes.func.isRequired,
            asyncUpdateRunbook: PropTypes.func.isRequired,
            updateRunbook: PropTypes.func.isRequired,
            copyRunbook: PropTypes.func.isRequired,
            exportRunbook: PropTypes.func.isRequired,
            exportListOfRunbooks: PropTypes.func.isRequired,
            setActiveRunbook: PropTypes.func.isRequired,
        }).isRequired,
    }

    state = {
        activeTab: 'all-runbooks',
        isCreating: false,
        isCreatingTemplate: false,
        isCopying: false,
        isEditing: false,
        runbookToDelete: null,
        editingId: null,
        tempName: '',
        isChangingAdmins: false,
        adminTeams: [],
        adminUsers: [],
        sortBy: null,
        sortDirection: null,
    }

    componentDidMount() {
        document.title = 'List of Runbooks'
        const { page, pathname } = this.props
        const isTemplatesPath = pathname === paths.TEMPLATES_PATH

        if (isTemplatesPath) {
            this.setState({ activeTab: 'templates' })
            return
        }

        this.props.actions.getRunbooksFromServer({ page })
    }

    handleCreateRunbook = () => {
        const { actions, dispatch } = this.props

        actions.setActiveRunbook(null)
        dispatch({ type: RESET_CREATE_RUNBOOK, runbookId: null })

        this.setState({ isCreating: true })
    }

    handleCreateRunbookTemplate = () => {
        const { dispatch } = this.props

        dispatch({ type: RESET_CREATE_TEMPLATE })

        this.setState({ isCreatingTemplate: true })
    }

    handleTabChange = (activeTab) => {
        const nextPath =
            activeTab === 'templates' ? paths.TEMPLATES_PATH : paths.LIST_PATH

        this.props.dispatch(push(nextPath))
        this.setState({ activeTab })

        if (activeTab === 'all-runbooks') {
            this.props.actions.getRunbooksFromServer({ page: this.props.page })
        }
    }

    handleExportListOfRunbooks = () => {
        this.props.actions.exportListOfRunbooks()
    }

    handleCancelCreateRunbook = () => {
        const { dispatch } = this.props

        this.setState({ isCreating: false })
        dispatch({ type: RESET_CREATE_RUNBOOK, runbookId: null })
    }

    handleCancelCreateTemplate = () => {
        const { dispatch } = this.props

        this.setState({ isCreatingTemplate: false })
        dispatch({ type: RESET_CREATE_TEMPLATE })
    }

    handleSaveTemplate = async (payload) => {
        const { actions, dispatch } = this.props

        try {
            await actions.createRunbookTemplate(payload)
            this.setState({ isCreatingTemplate: false })
            dispatch({ type: RESET_CREATE_TEMPLATE })
        } catch (_error) {
            return
        }
    }

    handleEditStart = (runbook) =>
        this.setState({
            isEditing: true,
            editingId: runbook.id,
            tempName: runbook.name,
        })

    handleCopyStart = (runbook) =>
        this.setState({
            isCopying: true,
            isEditing: false,
            editingId: runbook.id,
            tempName: runbook.name,
        })

    handleEditReset = () =>
        this.setState({
            isCopying: false,
            isEditing: false,
            editingId: null,
            tempName: '',
        })

    handleEditAdminsStart = (runbook) =>
        this.setState({
            adminUsers: runbook.admins,
            adminTeams: runbook.adminTeams,
            isChangingAdmins: true,
            editingId: runbook.id,
        })

    handleEditAdminsCancel = () =>
        this.setState({
            isChangingAdmins: false,
            adminUsers: [],
            adminTeams: [],
            editingId: null,
        })

    handleEditAdminsConfirm = (users, teams) => {
        this.props.actions.updateRunbook(this.state.editingId, {
            admins: users.map((user) => user.id),
            adminTeams: teams.map((team) => team.id),
        })
        this.props.actions.asyncUpdateRunbook(this.state.editingId)
        this.setState({
            isChangingAdmins: false,
            editingId: null,
            adminUsers: [],
            adminTeams: [],
        })
    }

    handleEnvironmentSelect = (runbookId, env) => () => {
        this.props.actions.updateRunbook(runbookId, {
            stage: env,
        })
        this.props.actions.asyncUpdateRunbook(runbookId)
        this.props.actions.getRunbooksFromServer()
    }

    handleConfirmDelete = () => {
        const { runbookToDelete } = this.state
        const { actions } = this.props

        actions.deleteRunbook(runbookToDelete.id)
        this.setState({ runbookToDelete: null })
    }

    handleCancelDelete = () => {
        this.setState({ runbookToDelete: null })
    }

    handleRunbookSave = () => {
        const { isCopying, isEditing, editingId, tempName } = this.state
        const {
            actions: { updateRunbook, asyncUpdateRunbook, copyRunbook },
        } = this.props
        if (isEditing) {
            updateRunbook(editingId, { name: tempName })
            asyncUpdateRunbook(editingId)
        }
        if (isCopying) {
            copyRunbook(editingId, tempName)
        }

        this.handleEditReset()
    }

    handleTempNameChange = (e) => {
        this.setState({ tempName: e.target.value })
    }

    renderRenameOrCopyDialog() {
        const { isEditing, isCopying } = this.state
        return (
            <Dialog
                title="Edit Runbook"
                isOpen={isEditing || isCopying}
                onClose={this.handleEditReset}
            >
                <div className={Classes.DIALOG_BODY}>
                    <InputGroup
                        onChange={this.handleTempNameChange}
                        value={this.state.tempName}
                    />
                </div>
                <div className={Classes.DIALOG_FOOTER}>
                    <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                        <Button
                            intent="primary"
                            large
                            onClick={this.handleRunbookSave}
                        >
                            Save
                        </Button>
                        <Button
                            intent="lynch-underline"
                            large
                            onClick={this.handleEditReset}
                        >
                            Close
                        </Button>
                    </div>
                </div>
            </Dialog>
        )
    }

    renderEditTeamsDialog() {
        const { isChangingAdmins, editingId } = this.state
        return (
            isChangingAdmins && (
                <AdminEditingDialog
                    runbook={
                        this.props.runbooks.filter(
                            (rbk) => rbk.id === editingId
                        )[0]
                    }
                    onCloseClick={this.handleEditAdminsCancel}
                    onConfirm={this.handleEditAdminsConfirm}
                />
            )
        )
    }

    renderNoRunbooks(runbooks) {
        const { loading, customerFilter } = this.props

        if (!runbooks.length && !loading && customerFilter.length)
            return (
                <DataPlaceholder
                    title="No results found."
                    img={noRunbooksImage}
                />
            )

        if (!runbooks.length && !loading)
            return (
                <DataPlaceholder
                    title="No Runbook have been created yet."
                    subtitle={
                        <>
                            Click at the right top corner on{' '}
                            <b>“Create new Runbook”</b> button to start creating
                            your first Runbook.
                        </>
                    }
                    img={noRunbooksImage}
                />
            )

        return (
            loading && (
                <div className="full-width" style={{ marginTop: '10%' }}>
                    <Spinner size={Spinner.SIZE_STANDARD} />
                </div>
            )
        )
    }

    _renderID({ cell }) {
        return <CustomTag>{cell}</CustomTag>
    }

    _renderType({ cell }) {
        return runbookModules.MODES.byId[cell].text
    }

    _renderRunbookName({ cell, row }) {
        return (
            <div className={styles.runbookNameCell}>
                <Link to={`${paths.LIST_PATH}${row.id}`}>{cell}</Link>

                {row.parameters?.length > 0 ? (
                    <CustomTag
                        size="small"
                        color="light-grey"
                        className={styles.parametersTag}
                    >
                        {row.parameters.join(' ')}
                    </CustomTag>
                ) : null}
                {row.hasFilters ? (
                    <CustomTag
                        size="small"
                        color="blue"
                        className={classNames(
                            styles.parametersTag,
                            styles.filterTag
                        )}
                        isRound
                    >
                        Filters installed
                    </CustomTag>
                ) : null}
            </div>
        )
    }

    _renderOwner({ cell }) {
        return cell.fullName
    }

    _renderStatus({ cell }) {
        if (cell) {
            return (
                <Tag round intent="warning">
                    In Progress
                </Tag>
            )
        }

        return (
            <Tag round intent="none" minimal className={styles.stoppedTag}>
                Stopped
            </Tag>
        )
    }

    _renderLifecycle = ({ row }) => {
        const IconMapping = {
            0: lifecycleIcons.circleIcon,
            1: lifecycleIcons.triangleIcon,
            2: lifecycleIcons.squareIcon,
            4: lifecycleIcons.octagonIcon,
        }
        const stage = runbookModules.STAGE_OPTIONS.find(
            (el) => el.value === row?.stage
        )
        if (stage === undefined) {
            console.warn(
                `Something went wrong while getting the icon for the stage of runbook with id=${row.id}. Check the mapping.`
            )
        }
        return IconMapping[stage?.value]
    }

    _renderMenu = ({ row: runbook }) => {
        const hasAvailableActions =
            runbook.isAdmin ||
            runbook.isOwner ||
            this.props.currentUserIsGlobalAdmin
        const stageOptions = runbookModules.STAGE_OPTIONS.filter(
            (env) => env.value !== undefined && env.label !== 'All'
        )

        return (
            <IconMenu
                icon={
                    <FontAwesomeIcon
                        icon={faEllipsisVertical}
                        className={styles.rowIconMenu}
                    />
                }
                // disabled={!hasAvailableActions}
                className={styles.rowMenu}
                content={
                    <Menu>
                        <MenuItem
                            text="Copy"
                            icon={'duplicate'}
                            onClick={() => this.handleCopyStart(runbook)}
                        />
                        {hasAvailableActions ? (
                            <>
                                <MenuItem
                                    text="Export runbook"
                                    icon="export"
                                    onClick={() => {
                                        this.props.actions.exportRunbook(
                                            runbook.id,
                                            runbook.name
                                        )
                                        this.props.dispatch({
                                            type: actionTypes.TOAST_SHOW,
                                            message: {
                                                content: `The export has started, it will create a download file shortly.`,
                                                intent: Intent.PRIMARY,
                                            },
                                        })
                                    }}
                                />
                                <MenuItem
                                    text="Edit admins"
                                    icon="edit"
                                    onClick={() =>
                                        this.handleEditAdminsStart(runbook)
                                    }
                                />
                                <MenuItem
                                    text="Rename"
                                    icon="new-text-box"
                                    onClick={() =>
                                        this.handleEditStart(runbook)
                                    }
                                />
                                <MenuItem
                                    text="Delete"
                                    icon="trash"
                                    onClick={() =>
                                        this.setState({
                                            runbookToDelete: runbook,
                                        })
                                    }
                                />
                                <MenuItem
                                    text="Lifecycle"
                                    icon={
                                        <FontAwesomeIcon
                                            icon={faTrafficLight}
                                            // FIXME: this needs to be removed when blueprintjs icons will be replaced with fontawesome one
                                            style={{
                                                fontSize: 16,
                                                margin: '2px 9px 0 2px',
                                            }}
                                        />
                                    }
                                >
                                    {stageOptions.map((env) => (
                                        <MenuItem
                                            key={env.value}
                                            text={env.label}
                                            icon={
                                                runbook.stage === env.value
                                                    ? 'tick'
                                                    : ''
                                            }
                                            onClick={this.handleEnvironmentSelect(
                                                runbook.id,
                                                env.value
                                            )}
                                        />
                                    ))}
                                </MenuItem>
                            </>
                        ) : null}
                    </Menu>
                }
            />
        )
    }

    loadRunbooks(page, sortBy, sortDirection) {
        if (sortBy) {
            const sort = `${sortDirection === 1 ? '' : '-'}${sortBy}`

            this.props.actions.getRunbooksFromServer({
                page: page,
                per_page: this.props.perPage,
                sort,
            })
        } else {
            this.props.actions.getRunbooksFromServer({
                page: page,
                per_page: this.props.perPage,
            })
        }
    }

    handleSortColumn = (column) => {
        let sortDirection = this.state.sortDirection
        let sortBy = column

        if (this.state.sortBy === sortBy) {
            if (sortDirection === -1) {
                sortBy = null
                sortDirection = null
            } else {
                sortDirection = -1
            }
        } else {
            sortDirection = 1
        }

        this.loadRunbooks(this.props.page, sortBy, sortDirection)

        this.setState({
            sortBy: sortBy,
            sortDirection: sortDirection,
        })
    }

    getTabState = () => {
        const { activeTab } = this.state
        const { pathname } = this.props
        const routeTab =
            pathname === paths.TEMPLATES_PATH ? 'templates' : 'all-runbooks'
        const availableTabs = getVisibleTabs(RUNBOOKS_TABS, this.context)
        const selectedTabId = getResolvedSelectedTabId(
            availableTabs,
            routeTab || activeTab
        )
        return { availableTabs, selectedTabId }
    }

    renderAllRunbooksTabContent = () => {
        const {
            runbooks,
            page,
            perPage,
            totalElements,
            totalPages,
        } = this.props
        const filteredRunbooks = runbooks.filter((runbook) => runbook.id)

        return (
            <>
                <RunbooksFilters />
                {this.renderNoRunbooks(filteredRunbooks) || (
                    <Pagination
                        currentPage={page}
                        totalElements={totalElements}
                        totalPages={totalPages}
                        perPage={perPage}
                        onPageChange={(newPage) => {
                            this.setCurrentPage(newPage)
                        }}
                        renderPage={this.renderRunbookPage}
                    />
                )}
            </>
        )
    }

    renderTemplatesTabContent = () => {
        return <RunbooksTemplates />
    }

    getActiveTabComponent = () => {
        const { selectedTabId } = this.getTabState()

        const tabComponents = {
            'all-runbooks': this.renderAllRunbooksTabContent,
            templates: this.renderTemplatesTabContent,
        }

        const component = tabComponents[selectedTabId]
        return component ? component() : null
    }

    renderRunbookPage = () => {
        const { sortBy, sortDirection } = this.state
        const { runbooks } = this.props
        const filteredRunbooks = runbooks.filter((runbook) => runbook.id)
        return (
            <div className={styles.listWrapper}>
                <List
                    onSort={this.handleSortColumn}
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    // [id] key is used to have a unique identifier used when rendering with map function
                    config={[
                        {
                            id: 'id',
                            name: 'ID',
                            accessor: 'id',
                            width: '5%',
                            cell: this._renderID,
                            sortable: true,
                        },
                        {
                            id: 'organization',
                            name: 'Organization',
                            accessor: 'organization',
                            width: '10%',
                            sortable: true,
                            sortColumn: 'customer__name',
                        },
                        {
                            id: 'type',
                            name: 'Type',
                            accessor: 'mode',
                            width: '11%',
                            cell: this._renderType,
                            sortable: true,
                        },
                        {
                            id: 'runbook-name',
                            name: 'Runbook name',
                            accessor: 'name',
                            width: '37%',
                            cell: this._renderRunbookName,
                            sortable: true,
                        },
                        {
                            id: 'modification-time',
                            name: 'Modification time',
                            accessor: 'lastModified',
                            cell: ({ cell }) => {
                                return moment(cell).format(TIMESTAMP_FORMAT)
                            },
                            width: '15%',
                            sortable: true,
                            sortColumn: 'last_updated',
                        },
                        {
                            id: 'owner',
                            name: 'Owner',
                            accessor: 'createdBy',
                            width: '10%',
                            cell: this._renderOwner,
                            sortable: true,
                            sortColumn: 'full_name',
                        },
                        {
                            id: 'status',
                            name: 'Status',
                            accessor: 'activeSession',
                            width: '7%',
                            cell: this._renderStatus,
                            sortable: true,
                            sortColumn: 'active_session_sort',
                        },
                        {
                            id: 'lifecycle-icon',
                            width: '3%',
                            cell: this._renderLifecycle,
                        },
                        {
                            id: 'menu-icon',
                            width: '2%',
                            cell: this._renderMenu,
                        },
                    ]}
                    data={filteredRunbooks}
                />
            </div>
        )
    }

    renderTabButtons = () => {
        const { selectedTabId } = this.getTabState()
        const canMaintainTemplates = isAllowed(
            this.context,
            RUNBOOKS_TEMPLATES_MAINTAIN
        )

        const buttonConfig = {
            templates: canMaintainTemplates
                ? [
                      {
                          text: 'Create runbook template',
                          icon: faCirclePlus,
                          onClick: this.handleCreateRunbookTemplate,
                          className: styles.createButton,
                      },
                  ]
                : [],
            'all-runbooks': [
                {
                    text: 'Create new Runbook',
                    icon: faCirclePlus,
                    onClick: () => this.handleCreateRunbook(),
                    className: styles.createButton,
                },
                {
                    text: 'Export runbooks',
                    icon: faFileExport,
                    onClick: () => this.handleExportListOfRunbooks(),
                    className: styles.listExportButton,
                },
            ],
        }

        const buttons = buttonConfig[selectedTabId] || []

        return (
            <>
                {buttons.map((btn) => (
                    <Button
                        key={btn.text}
                        intent="primary"
                        icon={<FontAwesomeIcon icon={btn.icon} />}
                        large
                        className={btn.className}
                        onClick={btn.onClick}
                    >
                        {btn.text}
                    </Button>
                ))}
            </>
        )
    }

    setCurrentPage = (newPage) => {
        const { dispatch } = this.props

        // dispatch action to change the state variable 'page'
        dispatch({
            type: actionTypes.CHANGE_RUNBOOKS_PAGE,
            page: newPage,
        })

        this.loadRunbooks(newPage, this.state.sortBy, this.state.sortDirection)

        // reset the active runbook
        this.props.actions.setActiveRunbook(null)
    }

    render() {
        const { runbookToDelete, isCreating, isCreatingTemplate } = this.state
        const { selectedTabId } = this.getTabState()

        return (
            <div className={styles.runbooksWrapper}>
                <p className={styles.pageTitle}>List of Runbooks</p>
                <div className={styles.tabs}>
                    <PageTabs
                        id="runbooks-overview-tabs"
                        selectedTabId={selectedTabId}
                        tabs={RUNBOOKS_TABS}
                        permissionContext={this.context}
                        onChange={this.handleTabChange}
                    />
                    <div>{this.renderTabButtons()}</div>
                </div>

                <Card>{this.getActiveTabComponent()}</Card>

                {this.renderRenameOrCopyDialog()}
                {this.renderEditTeamsDialog()}

                <ConfirmDialog
                    title="Are you sure you want to delete this runbook?"
                    isOpen={!!runbookToDelete}
                    confirmLabel="Delete"
                    confirmIntent="danger"
                    onCancel={this.handleCancelDelete}
                    onConfirm={this.handleConfirmDelete}
                />

                {isCreating && (
                    <CreateRunbookModal
                        onCancel={this.handleCancelCreateRunbook}
                    />
                )}

                {isCreatingTemplate && (
                    <CreateTemplateModal
                        onCancel={this.handleCancelCreateTemplate}
                        onSave={this.handleSaveTemplate}
                    />
                )}
            </div>
        )
    }
}

const mapStateToProps = (state) => {
    return {
        pathname: state.router.location.pathname,
        runbooks: runbookModules.getRunbooks(state),
        loading: state.runbooks.loading,
        currentUser: state.app.user,
        currentUserIsGlobalAdmin: state.app.isAdmin,
        customerFilter: state.runbooks.customerFilter,
        page: state.runbooks.page,
        totalElements: state.runbooks.totalElements,
        totalPages: state.runbooks.totalPages,
        perPage: state.runbooks.perPage,
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        dispatch,
        actions: bindActionCreators(
            {
                deleteRunbook: (id) =>
                    runbookModules.deleteRunbookFromServer(dispatch, id),
                getRunbooksFromServer: runbookModules.getRunbooksFromServer,
                createRunbookTemplate: runbookModules.createRunbookTemplate,
                asyncUpdateRunbook: runbookModules.asyncUpdateRunbook,
                updateRunbook: runbookModules.updateRunbook,
                copyRunbook: runbookModules.copyRunbook,
                exportRunbook: runbookModules.exportRunbook,
                exportListOfRunbooks: runbookModules.exportListOfRunbooks,
                setActiveRunbook,
            },
            dispatch
        ),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(RunbooksList)
